import React from "react";
import { Calculator } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip.jsx";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig.js"
import pm from './images/tennis-pm.webp';
import t1 from './images/tennis-pm-dj.png';
import t2 from './images/tennis-b365-dj.png';
import tc from './images/tennisc.png';

const DutchingGuide = () => {
  const meta = guidesConfig.dutchingGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.dutchingGuide?.route || "/guides"}
      />

      <p>
      Dutching is a valuable strategy in matched betting that helps you stay profitable when lay betting isn't possible. Whether you’re trying to dodge tennis retirement rule mismatches or there’s no exchange market for an obscure bet, dutching gives you another way to qualify for free bets and extract value from bookmaker offers.      </p>

      <h2>What Is Dutching?</h2>
      <h6 className="info">Dutching means <span className="back">backing</span> all outcomes, adjusting the stakes so that the total return is the same, regardless of who wins.</h6>
      <p>Dutching only involves <span className="back">back</span> bets. We don't need to <span className="lay">lay</span> anything at an exchange.</p>


      <h2>When Should You Use Dutching?</h2>
      <ul>
        <li>
          <strong>No matching lay market:</strong> Some promos are on niche markets (e.g., 'team to score first penalty') where exchanges don't offer matching markets. You can find odds at multiple bookies and use dutching instead.
        </li>
        <li>
          <strong>Rule mismatch:</strong> There might be differing rules between the bookie and the exchanges. The most common example is tennis retirement rules. Bookies often void bets on retirements, but exchanges might settle them. If the rules don’t align, dutching between two bookies that share the same policy avoids nasty surprises.
        </li>
        <li>
          <strong>Matched betting abroad:</strong> If Betfair or Smarkets isn’t available in your country, dutching lets you continue matched betting using only bookmakers.
        </li>
        <li>
          <strong>Gubbed accounts:</strong> Got accounts that don’t offer promos anymore? Use them to dutch selections and still make use of their odds.
        </li>
      </ul>

   

      <h2>How to Dutch a Bet</h2>
      <p>Let's imagine we want to qualify for this offer from Parimatch:</p>
      <img src={pm} alt="matched betting Parimatch offer" />
      <p>We need to bet £10 on tennis at odds 2.0+.</p>
      <p>We find an upcoming match between Novak Djokovic and Alexander Zverev:</p>
      <div className="grid-fixed g2">
        <div className="col">
            <h3>Parimatch:</h3>
            <img src={t1} alt="Parimatch tennis match odds" />
            <p>We need to bet £10 on tennis at odds 2.0+, so we could <span className="back">back</span> <span className="key">Zverev</span> at odds of 2.3 for £10.</p>

        </div>
        <div className="col">
            <h3>Bet365:</h3>
            <img src={t2} alt="Bet365 tennis match odds" />
            <p>We need to cover all outcomes, so we find the same match on Bet365 and find the odds for <span className="key">Djokovic</span> are 1.75.</p>

        </div>
      </div>
      <ol>
  <li>Use the Dutching Calculator to work out your stakes:</li>
  <div className="grid-fixed g2">
        <div className="col">
            <img src={tc} alt="Matched betting Dutch calculator" />

        </div>
        <div className="col">
            <p>We want the <span className="key">stake</span> for our first selection, <span className="key">Zverev</span>, to be £10.</p>
            <p>We input the odds of 2.3 for <span className="key">Zverev</span>.</p>
            <p>We input the odds of 1.75 for <span className="key">Djokovic</span>.</p>
            <p>The calculator tells us to <span className="back">back</span> <span className="key">Djokovic</span> for £13.14.</p>
        </div>
      </div>

  <li>Place the first bet on Parimatch: place a £10 <span className="back">back</span> bet on <span className="key">Zverev</span> at odds of 2.3.</li>
  <li>Place the second bet on Bet365: place a £13.14 <span className="back">back</span> bet on <span className="key">Djokovic</span> at odds of 1.75.</li>
  <li>Your qualifying loss is 14p, and you have now qualified for the £40 in free bets.</li>
</ol>

      <table>
        <thead>
          <tr>
            <th>Outcome</th>
            <th>First bet</th>
            <th>Second bet</th>
            <th>Profit/Loss</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Zverev</td>
            <td>£13.00<br></br>Excl. stake</td>
            <td>-£13.14</td>
            <td>–£0.14</td>
          </tr>
          <tr>
            <td>Djokovic</td>
            <td>-£13.14</td>
            <td>£13.00<br></br>Excl. stake</td>
            <td>–£0.14</td>
          </tr>
        </tbody>
      </table>
      <CTALarge
        to="/dutching-calculator"
        text="Try Our Dutching Calculator"
        icon={Calculator}
      />
      <h2>Using the Dutching Calculator</h2>
<p>
  Our calculator lets you choose a <Tooltip content="Fix the stake on your initial selection">First Selection Stake</Tooltip>,
  or a <Tooltip content="Limit the total amount you stake across all bets">Total Stake</Tooltip>.
</p>
<ul>
  <li>
    <strong>First Selection Stake</strong>: You enter the exact amount you’ve already bet on one outcome. The calculator then works out how much to stake on the other selections so that your return is the same no matter which one wins.
  </li>
  <li>
    <strong>Total Stake</strong>: You decide the total you want to spend across all bets. The calculator splits that sum over each selection. This helps you control your outlay and stick to your budget.
  </li>
</ul>

      <p>
  Dutching isn’t just for events with two outcomes. You can back any number of selections — whether that’s sfootball match odds, or ten runners in a horse race. The Dutching Calculator handles the maths for you, splitting your total stake (or fixing your desired profit) across every outcome. This way, you lock in the same return — or a small, controlled qualifying loss — no matter which selection wins.
</p>



      <h2>Arbitrage Dutching</h2>
      <p>
        When the <Tooltip content="Total implied probability of odds across outcomes">implied probability</Tooltip> is under 100%, you’ve found an arb.
      </p>
      <p>Before you start, calculate the implied probability of each outcome:</p>
      <pre>
        <code>
        Implied probability = 100 ÷ decimal odds
        </code>
      </pre>
      <p>
      When the sum of these probabilities is less than 100%, you’ve found an arb. Next, use a dutching calculator to split your stakes so you lock in profit, whatever the result.      </p>
      <div className="example-container">
        <h4>example</h4>
        <ul>
            <li>
            Runner A @ 2.40 → 100 ÷ 2.40 = 41.67%
            </li>
            <li>Runner B @ 2.80 → 100 ÷ 2.80 = 35.71%</li>
            <li>Runner C @ 5.50 → 100 ÷ 5.50 = 18.18%</li>
            <p>Total implied probability = 95.56% → Arb opportunity</p>
        </ul>
      </div>
      <p>Use the calculator to:</p>
      <ol>
        <li>Set your Total Stake across all bets.</li>
        <li>Or fix an Overall Profit target.</li>
      </ol>
      <p>The tool will show you exactly how much to stake on each selection. Back those bets at different bookies to guarantee your profit.</p>
      <h6 className="warn">Reserve arbing for accounts you don’t mind using heavily. Frequent arbing on main accounts can lead to gubbing.</h6>

      <h2>Final Thoughts</h2>
      <p>
        Dutching keeps your matched betting moving when laying isn’t an option. Use it for tricky promos, niche markets or overseas betting. Calculate carefully to unlock more value from every offer.
      </p>
    </div>
  );
};

export default DutchingGuide;
